import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/NotificationBell";
import { LayoutDashboard, LogOut, Wallet, FileKey, Inbox, Search, Wand2, Fingerprint } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navLink = (to, label, icon) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase()}-link`}
      className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-[background-color,color] hover:bg-muted ${
        location.pathname.startsWith(to) ? "text-foreground bg-muted" : "text-muted-foreground"
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur-md">
      <div className="page-container flex h-16 items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="font-display text-2xl tracking-[0.06em] text-foreground">
          LOOKBOOK
        </Link>
        <nav className="flex items-center gap-1">
          {navLink("/explore", "Explore", <Search className="h-4 w-4" />)}
          {user?.role === "client" && navLink("/studio", "Studio", <Wand2 className="h-4 w-4" />)}
          {user && ["model", "agency"].includes(user.role) && navLink("/twin", "Twin", <Fingerprint className="h-4 w-4" />)}
          {user && navLink("/bookings", "Bookings", <Inbox className="h-4 w-4" />)}
          {user && navLink("/licenses", "Licenses", <FileKey className="h-4 w-4" />)}
          {user && navLink("/wallet", "Wallet", <Wallet className="h-4 w-4" />)}
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu-button" className="ml-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border">
                    <AvatarFallback className="bg-accent text-accent-foreground text-sm font-semibold">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold">{user.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user.role} account</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-dashboard-menuitem" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout-menuitem" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="nav-signin-button" onClick={() => navigate("/auth")} className="ml-2 rounded-xl">
              Sign in
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};
